/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  Copy,
  Users,
  ChevronRight,
  CheckSquare,
  Search,
  Save,
  Loader2,
  Lock,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';



// Module display names and icons
const MODULE_META = {
  dashboard: { label: 'Kontrol Paneli', color: 'bg-blue-500' },
  tickets: { label: 'Vatandas Katilim', color: 'bg-amber-500' },
  stk: { label: 'STK Basvurulari', color: 'bg-emerald-500' },
  meetings: { label: 'Toplantilar', color: 'bg-violet-500' },
  announcements: { label: 'Duyurular', color: 'bg-rose-500' },
  wg: { label: 'Calisma Gruplari', color: 'bg-cyan-500' },
  users: { label: 'Kullanici Yonetimi', color: 'bg-orange-500' },
  organs: { label: 'Organ Yapisi', color: 'bg-indigo-500' },
  roles: { label: 'Rol Yonetimi', color: 'bg-pink-500' },
  notifications: { label: 'Bildirimler', color: 'bg-teal-500' },
  reports: { label: 'Raporlar', color: 'bg-lime-600' },
  logs: { label: 'Sistem Loglari', color: 'bg-gray-500' },
  muhtar: { label: 'Muhtar Islemleri', color: 'bg-yellow-600' },
  surveys: { label: 'Anketler', color: 'bg-sky-500' },
};

// ─── Role Card ────────────────────────────────────
const RoleCard = ({ role, isSelected, onClick, onEdit, onDelete, onCopy }) => (
  <div
    onClick={onClick}
    data-testid={`role-card-${role.name}`}
    className={`group relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
      isSelected
        ? 'border-blue-500 bg-blue-50/80 shadow-md'
        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
    }`}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-blue-600' : 'bg-slate-100'}`}>
          <Shield className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">{role.display_name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{role.name}</p>
        </div>
      </div>
      {role.is_system && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50">
          <Lock className="w-2.5 h-2.5 mr-0.5" /> Sistem
        </Badge>
      )}
    </div>
    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <ShieldCheck className="w-3.5 h-3.5" /> {role.permission_count} yetki
      </span>
      <span className="flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {role.user_count} kullanici
      </span>
    </div>
    {/* Actions */}
    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
      <button onClick={() => onCopy(role)} className="p-1.5 rounded-md hover:bg-slate-100" title="Kopyala" data-testid={`role-copy-${role.name}`}>
        <Copy className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {!role.is_system && (
        <>
          <button onClick={() => onEdit(role)} className="p-1.5 rounded-md hover:bg-slate-100" title="Duzenle" data-testid={`role-edit-${role.name}`}>
            <Pencil className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <button onClick={() => onDelete(role)} className="p-1.5 rounded-md hover:bg-red-50" title="Sil" data-testid={`role-delete-${role.name}`}>
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </>
      )}
    </div>
  </div>
);

// ─── Permission Module Block ──────────────────────
const PermissionModule = ({ module, permissions, selectedIds, onToggle, onToggleAll }) => {
  const meta = MODULE_META[module] || { label: module, color: 'bg-slate-500' };
  const allChecked = permissions.every(p => selectedIds.has(p.id));
  const someChecked = permissions.some(p => selectedIds.has(p.id)) && !allChecked;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`perm-module-${module}`}>
      {/* Module header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${meta.color}`} />
          <span className="font-semibold text-sm text-slate-800">{meta.label}</span>
          <Badge variant="secondary" className="text-[10px]">{permissions.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Tumunu sec</span>
          <Checkbox
            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
            onCheckedChange={() => onToggleAll(module, permissions, !allChecked)}
            data-testid={`perm-select-all-${module}`}
          />
        </div>
      </div>
      {/* Permissions grid */}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {permissions.map(perm => (
          <label
            key={perm.id}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150 ${
              selectedIds.has(perm.id)
                ? 'border-blue-200 bg-blue-50/50'
                : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`perm-checkbox-${perm.code}`}
          >
            <Checkbox
              checked={selectedIds.has(perm.id)}
              onCheckedChange={() => onToggle(perm.id)}
              className="mt-0.5"
            />
            <div>
              <p className="text-sm font-medium text-slate-800">{perm.display_name}</p>
              <p className="text-[11px] text-slate-400 font-mono mt-0.5">{perm.code}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

// ─── Main RBAC Page ───────────────────────────────
export default function RBACPage() {
  const { hasPermission } = useAuth();
  usePageTitle('Rol Yonetimi');
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermIds, setSelectedPermIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [roleDialog, setRoleDialog] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', display_name: '', description: '' });
  const [editingRole, setEditingRole] = useState(null);
  const [copyDialog, setCopyDialog] = useState(false);
  const [copyForm, setCopyForm] = useState({ new_name: '', new_display_name: '' });
  const [copySource, setCopySource] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canEdit = hasPermission('roles.edit');
  const canCreate = hasPermission('roles.create');
  const canDelete = hasPermission('roles.delete');
  const canAssign = hasPermission('roles.assign_permissions');

  // ─── Fetch data ────────────────────
  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/rbac/roles');
      setRoles(res.data);
    } catch (err) {
      toast.error('Roller yuklenemedi');
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/rbac/permissions');
      setModules(res.data);
    } catch (err) {
      toast.error('Yetkiler yuklenemedi');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchRoles(), fetchPermissions()]).finally(() => setLoading(false));
  }, [fetchRoles, fetchPermissions]);

  // ─── Select role → load its permissions ────────
  const handleSelectRole = useCallback(async (role) => {
    if (dirty) {
      if (!window.confirm('Kaydedilmemis degisiklikler var. Devam etmek istiyor musunuz?')) return;
    }
    setSelectedRole(role);
    try {
      const res = await api.get(`/rbac/roles/${role.id}`);
      setSelectedPermIds(new Set(res.data.permission_ids || []));
      setDirty(false);
    } catch {
      toast.error('Rol detaylari yuklenemedi');
    }
  }, [dirty]);

  // ─── Permission toggling ──────────────────────
  const togglePermission = (permId) => {
    setSelectedPermIds(prev => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
    setDirty(true);
  };

  const toggleModuleAll = (module, perms, selectAll) => {
    setSelectedPermIds(prev => {
      const next = new Set(prev);
      perms.forEach(p => selectAll ? next.add(p.id) : next.delete(p.id));
      return next;
    });
    setDirty(true);
  };

  const selectAllPermissions = () => {
    const allIds = modules.flatMap(m => m.permissions.map(p => p.id));
    setSelectedPermIds(new Set(allIds));
    setDirty(true);
  };

  const deselectAllPermissions = () => {
    setSelectedPermIds(new Set());
    setDirty(true);
  };

  // ─── Save permissions ─────────────────────────
  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await api.put(
        `/rbac/roles/${selectedRole.id}/permissions`,
        { permission_ids: Array.from(selectedPermIds) }
      );
      toast.success(`"${selectedRole.display_name}" yetkileri kaydedildi`);
      setDirty(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yetkiler kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // ─── Create / Edit role ───────────────────────
  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', display_name: '', description: '' });
    setRoleDialog(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, display_name: role.display_name, description: role.description || '' });
    setRoleDialog(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim() || !roleForm.display_name.trim()) {
      toast.error('Rol adi ve gorunen adi zorunludur');
      return;
    }
    try {
      if (editingRole) {
        await api.put(
          `/rbac/roles/${editingRole.id}`,
          { display_name: roleForm.display_name, description: roleForm.description }
        );
        toast.success('Rol guncellendi');
      } else {
        await api.post('/rbac/roles', roleForm);
        toast.success('Yeni rol olusturuldu');
      }
      setRoleDialog(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rol kaydedilemedi');
    }
  };

  // ─── Copy role ────────────────────────────────
  const openCopyRole = (role) => {
    setCopySource(role);
    setCopyForm({ new_name: `${role.name}_kopya`, new_display_name: `${role.display_name} (Kopya)` });
    setCopyDialog(true);
  };

  const handleCopyRole = async () => {
    try {
      await api.post(
        '/rbac/roles/copy',
        { source_role_id: copySource.id, new_name: copyForm.new_name, new_display_name: copyForm.new_display_name }
      );
      toast.success('Rol kopyalandi');
      setCopyDialog(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rol kopyalanamadi');
    }
  };

  // ─── Delete role ──────────────────────────────
  const openDeleteRole = (role) => {
    setDeleteTarget(role);
    setDeleteDialog(true);
  };

  const handleDeleteRole = async () => {
    try {
      await api.delete(`/rbac/roles/${deleteTarget.id}`);
      toast.success('Rol silindi');
      setDeleteDialog(false);
      if (selectedRole?.id === deleteTarget.id) {
        setSelectedRole(null);
        setSelectedPermIds(new Set());
      }
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rol silinemedi');
    }
  };

  // ─── Filter permissions by search ─────────────
  const filteredModules = modules.map(m => ({
    ...m,
    permissions: m.permissions.filter(p =>
      !searchQuery ||
      p.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.code.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(m => m.permissions.length > 0);

  const totalPerms = modules.reduce((sum, m) => sum + m.permissions.length, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="rbac-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rbac-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rol ve Yetki Yonetimi</h1>
          <p className="text-sm text-slate-500 mt-1">
            {roles.length} rol, {totalPerms} yetki tanimli
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreateRole} data-testid="create-role-btn">
            <Plus className="w-4 h-4 mr-2" /> Yeni Rol
          </Button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Roles */}
        <div className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-3">
          <div className="sticky top-20">
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Roller
            </h2>
            <div className="space-y-2 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
              {roles.map(role => (
                <RoleCard
                  key={role.id}
                  role={role}
                  isSelected={selectedRole?.id === role.id}
                  onClick={() => handleSelectRole(role)}
                  onEdit={openEditRole}
                  onDelete={openDeleteRole}
                  onCopy={openCopyRole}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Permissions Matrix */}
        <div className="col-span-12 lg:col-span-8 xl:col-span-9">
          {selectedRole ? (
            <div className="space-y-4">
              {/* Selected role header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{selectedRole.display_name}</h2>
                    <p className="text-xs text-slate-500">
                      {selectedPermIds.size} / {totalPerms} yetki secili
                      {dirty && <span className="text-amber-600 ml-2 font-medium">* Kaydedilmemis degisiklikler</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canAssign && (
                    <>
                      <Button variant="outline" size="sm" onClick={selectAllPermissions} data-testid="select-all-perms">
                        <CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Tumu
                      </Button>
                      <Button variant="outline" size="sm" onClick={deselectAllPermissions} data-testid="deselect-all-perms">
                        Temizle
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSavePermissions}
                        disabled={!dirty || saving}
                        data-testid="save-permissions-btn"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                        Kaydet
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Yetki ara..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="perm-search-input"
                />
              </div>

              {/* Permission modules */}
              <div className="space-y-4">
                {filteredModules.map(mod => (
                  <PermissionModule
                    key={mod.module}
                    module={mod.module}
                    permissions={mod.permissions}
                    selectedIds={selectedPermIds}
                    onToggle={canAssign ? togglePermission : () => {}}
                    onToggleAll={canAssign ? toggleModuleAll : () => {}}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="rbac-no-selection">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <ChevronRight className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-400">Bir rol secin</h3>
              <p className="text-sm text-slate-400 mt-1">Soldaki listeden bir rol secerek yetkilerini goruntuleyebilirsiniz</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Create / Edit Role Dialog ──────── */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Rol Duzenle' : 'Yeni Rol Olustur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingRole && (
              <div>
                <label className="text-sm font-medium text-slate-700">Rol Kodu (unique)</label>
                <Input
                  placeholder="ornek: ozel_yonetici"
                  value={roleForm.name}
                  onChange={e => setRoleForm(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                  data-testid="role-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700">Gorunen Ad</label>
              <Input
                placeholder="ornek: Ozel Yonetici"
                value={roleForm.display_name}
                onChange={e => setRoleForm(prev => ({ ...prev, display_name: e.target.value }))}
                data-testid="role-display-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Aciklama</label>
              <Input
                placeholder="Rol hakkinda kisa aciklama"
                value={roleForm.description}
                onChange={e => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="role-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)}>Iptal</Button>
            <Button onClick={handleSaveRole} data-testid="role-save-btn">
              {editingRole ? 'Guncelle' : 'Olustur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Copy Role Dialog ─────────────── */}
      <Dialog open={copyDialog} onOpenChange={setCopyDialog}>
        <DialogContent data-testid="copy-role-dialog">
          <DialogHeader>
            <DialogTitle>Rol Kopyala</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            "{copySource?.display_name}" rolunun tum yetkileri yeni role kopyalanacak.
          </p>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Yeni Rol Kodu</label>
              <Input
                value={copyForm.new_name}
                onChange={e => setCopyForm(prev => ({ ...prev, new_name: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                data-testid="copy-role-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Yeni Gorunen Ad</label>
              <Input
                value={copyForm.new_display_name}
                onChange={e => setCopyForm(prev => ({ ...prev, new_display_name: e.target.value }))}
                data-testid="copy-role-display-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialog(false)}>Iptal</Button>
            <Button onClick={handleCopyRole} data-testid="copy-role-save-btn">
              <Copy className="w-4 h-4 mr-2" /> Kopyala
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Role Dialog ───────────── */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="delete-role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Rol Sil
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            "<strong>{deleteTarget?.display_name}</strong>" rolunu silmek istediginize emin misiniz?
            {deleteTarget?.user_count > 0 && (
              <span className="block mt-2 text-amber-600">
                Bu role atanmis {deleteTarget.user_count} kullanici var. Once kullanicilarin rolunu degistirmeniz gerekir.
              </span>
            )}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Iptal</Button>
            <Button variant="destructive" onClick={handleDeleteRole} data-testid="delete-role-confirm-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
